import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Package, Truck, CheckCircle, Clock, ShoppingBag, Eye, ChevronDown, ChevronUp } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../hooks/useUser';
import ApiCheckService from '../services/apiCheck'

const Orders = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  const navigate = useNavigate();
  const { user, isAuthenticated } = useUser();
  const [orders, setOrders] = useState([]);
  const [orderTotals, setOrderTotals] = useState({
    totalPedidos: 0,
    enProceso: 0,
    entregados: 0,
    pendientes: 0
  });
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [error, setError] = useState(null);

  const loadOrders = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    setError(null);

    try {
      const response = await ApiCheckService.myOrders();

      setOrderTotals(response.totales);
      setOrders(response.ventas);

    } catch (err) {
      console.error("Error al cargar las órdenes:", err);
      setError("No se pudieron cargar tus pedidos. Por favor, inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    loadOrders(); // Inicia la carga de datos
  }, [isAuthenticated, navigate, loadOrders]);

  const getStatusInfo = (estado) => {
    const statusMap = {
      'Pendiente': {
        label: 'Pendiente',
        color: 'text-yellow-700 bg-yellow-100 border-yellow-200',
        icon: Clock
      },
      'En Proceso': { // Asumiendo que usas 'En Proceso' en tu backend
        label: 'En Proceso',
        color: 'text-blue-700 bg-blue-100 border-blue-200',
        icon: Package
      },
      'Enviado': { // Puedes tener un estado intermedio como este
        label: 'Enviado',
        color: 'text-purple-700 bg-purple-100 border-purple-200',
        icon: Truck
      },
      'Entregado': { // Asumiendo que usas 'Entregado' en tu backend
        label: 'Entregado',
        color: 'text-green-700 bg-green-100 border-green-200',
        icon: CheckCircle
      }
    };
    // Devuelve 'Pendiente' si el estado no coincide o no existe
    return statusMap[estado] || statusMap['Pendiente'];
  };

  const toggleOrderDetails = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando tus pedidos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg">
          <p className="text-red-600 font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Mis Pedidos
            </span>
          </h1>
          <p className="text-xl text-gray-600">
            Hola {user?.nombres || 'Usuario'}, aquí puedes ver el estado de todos tus pedidos
          </p>
        </motion.div>

        {/* Estadísticas Rápidas sdsdsd */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8"
        >
          {[
            { label: 'Total Pedidos', value: orderTotals.totalPedidos, icon: ShoppingBag, color: 'blue' },
            { label: 'En Proceso', value: orderTotals.enProceso, icon: Truck, color: 'purple' },
            { label: 'Entregados', value: orderTotals.entregados, icon: CheckCircle, color: 'green' },
            { label: 'Pendientes', value: orderTotals.pendientes, icon: Clock, color: 'yellow' }
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center gap-4">
                <div className={`bg-${stat.color}-100 rounded-xl p-3`}>
                  <stat.icon className={`w-6 h-6 text-${stat.color}-600`} />
                </div>
                <div>
                  <p className="text-sm text-gray-600">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Lista de Pedidos */}
        {orders.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-2xl p-12 text-center shadow-sm"
          >
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No tienes pedidos aún</h3>
            <p className="text-gray-600 mb-6">Explora nuestros productos y realiza tu primera compra</p>
            <Link
              to="/products"
              className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              Ver Productos
            </Link>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {orders.map((order, index) => {
              const statusInfo = getStatusInfo(order.estado);
              const StatusIcon = statusInfo.icon;
              const isExpanded = expandedOrder === order.id;

              const formattedDate = new Date(order.year, order.month - 1, order.day).toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              });

              const itemsCount = order.detalles?.length || 0;
              const currency = '$'; // O '$' si lo prefieres

              return (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-sm hover:shadow-lg transition-shadow overflow-hidden"
                >
                  {/* Encabezado del Pedido */}
                  <div className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold text-gray-900">{order.nventa}</h3>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium border ${statusInfo.color} flex items-center gap-1`}>
                            <StatusIcon className="w-4 h-4" />
                            {statusInfo.label}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm">
                          Fecha: {formattedDate}
                        </p>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Total</p>
                          <p className="text-2xl font-bold text-gray-900">{currency}{order.total.toFixed(2)}</p>
                        </div>
                        <button
                          onClick={() => toggleOrderDetails(order.id)}
                          className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                        >
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-gray-600" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-600" />
                          )}
                        </button>
                      </div>
                    </div>

                    {isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-6 pt-6 border-t border-gray-200"
                      >

                        <div className="mb-6">
                          <h4 className="font-semibold text-gray-900 mb-4">Productos ({itemsCount})</h4>
                          <div className="space-y-3">
                            {itemsCount > 0 ? order.detalles.map((detalle, detIndex) => (
                              <div key={detIndex} className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
                                <img
                                  src={`${API_BASE_URL}/producto/obtener_image_producto/` + detalle.producto.portada.split('/').pop() || '/api/placeholder/300/300'}
                                  alt={detalle.producto.titulo}
                                  className="w-16 h-16 object-cover rounded-lg"
                                />
                                <div className="flex-1">
                                  <p className="font-medium text-gray-900">{detalle.producto.titulo}</p>
                                  <p className="text-sm text-gray-600">Cantidad: {detalle.cantidad}</p>
                                </div>
                                <p className="font-semibold text-gray-900">{currency}{(detalle.precio_unidad * detalle.cantidad).toFixed(2)}</p>
                              </div>
                            )) : (
                              <p className="text-sm text-gray-500">No hay detalles de productos disponibles.</p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="p-4 bg-gray-50 rounded-xl">
                            <h4 className="font-semibold text-gray-900 mb-2">Dirección de Envío</h4>
                            {/* 🚨 Usamos el objeto direccion incluido */}
                            <p className="text-gray-600">{order.direccion.direccion}, {order.direccion.ciudad}, {order.direccion.pais}</p>
                            <p className="text-gray-600">Teléfono: {order.direccion.telefono}</p>
                          </div>
                          <div className="p-4 bg-gray-50 rounded-xl">
                            <h4 className="font-semibold text-gray-900 mb-2">Método de Pago</h4>
                            {/* 🚨 Usamos forma_pago, banco y transaccion */}
                            <p className="text-gray-600 font-semibold">{order.forma_pago}</p>
                            {(order.banco || order.transaccion) && (
                              <p className="text-sm text-gray-600">Ref: {order.transaccion} </p>
                            )}
                            <div className="mt-2 text-sm">
                              <p>Subtotal: {currency}{order.subtotal.toFixed(2)}</p>
                              <p>Envío: {currency}{order.envio.toFixed(2)}</p>
                              <p className="font-bold">Total: {currency}{order.total.toFixed(2)}</p>
                            </div>
                          </div>
                        </div>

                        <div className="mt-6 flex gap-3">
                          <button className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-xl font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2">
                            <Eye className="w-4 h-4" />
                            Ver Detalles Completos
                          </button>
                          {/* 🚨 Usamos el estado de tu backend */}
                          {order.estado === 'Entregado' && (
                            <button className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-xl font-semibold hover:bg-gray-50 transition-all">
                              Comprar de Nuevo
                            </button>
                          )}
                        </div>
                      </motion.div>
                    )}

                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;
